import { scaleOrdinal } from '@visx/scale';

export type PaletteOption = string[] | Record<string, string>;

export type CustomPalette = { palette?: PaletteOption };

/**
 * Get a color for the given datum based on config and theme
 * @param config Tile config
 * @param defaultPalette Default theme
 * @param id Datum ID
 * @param index Datum Index
 * @returns Color
 */
export const getColor = function <T extends CustomPalette>(
    config: T,
    defaultPalette: PaletteOption,
    id: string,
    index: number
) {
    const palette = config.palette ?? defaultPalette;

    if (Array.isArray(palette)) {
        return palette[index % palette.length];
    }

    return palette[id];
};

/**
 * Creates a color scale based on the provided palette and data keys.
 * @param palette Either the user-defined palette or the default theme palette.
 * @param dataKeys The keys of the data to be used for the color scale.
 * @returns A function that takes a key and returns the corresponding color.
 */
export const createColorScale = (palette: PaletteOption, dataKeys: string[]): ((key: string) => string) => {
    if (!Array.isArray(palette)) {
        return (key: string) => palette[key] ?? 'black';
    }

    return scaleOrdinal<string, string>({
        domain: dataKeys,
        range: palette
    });
};
